'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import { Lock, Mail, ArrowRight, Sparkles, UserCheck, CheckCircle2 } from 'lucide-react';

export default function IngresarPage() {
  const router = useRouter();
  const [email, setEmail] = useState('juan@appgenda.io');
  const [password, setPassword] = useState('admin123');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e?: React.FormEvent, customEmail?: string) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');

    const targetEmail = customEmail || email;

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, password: 'admin123' })
      });

      const data = await res.json();
      if (data.success) {
        router.push('/dashboard');
        router.refresh();
      } else {
        setError(data.error || 'Credenciales inválidas');
      }
    } catch (err: any) {
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7FA] text-[#191923] flex flex-col font-sans selection:bg-[#6255F5] selection:text-white">
      {/* Header */}
      <header className="w-full py-6 px-6 max-w-6xl mx-auto flex items-center justify-between">
        <CalenddyLogo href="/" size="md" />
        <Link
          href="/"
          className="text-xs font-semibold text-slate-500 hover:text-[#6255F5] transition"
        >
          ← Volver a inicio
        </Link>
      </header>

      {/* Login Card */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-slate-200/90 shadow-xl space-y-6">
          {/* Title */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ECEAFF] text-[#6255F5] text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              Acceso a tu Panel de Control
            </div>
            <h1 className="text-2xl font-black text-[#191923] tracking-tight">
              Ingresá a tu cuenta
            </h1>
            <p className="text-xs text-slate-500">
              Gestioná tu agenda, servicios, clientes y WhatsApp.
            </p>
          </div>

          {/* Quick Demo Access Switcher */}
          <div className="space-y-2 pt-1">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">
              Acceso Rápido de Prueba (1 clic):
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleLogin(undefined, 'juan@appgenda.io')}
                className="p-2.5 bg-slate-50 hover:bg-[#ECEAFF] border border-slate-200 hover:border-[#6255F5] rounded-xl text-left transition text-xs group cursor-pointer"
              >
                <div className="flex items-center gap-1 text-[#6255F5] font-bold">
                  <UserCheck className="w-3.5 h-3.5" />
                  <span>Juan Rojas</span>
                </div>
                <div className="text-[10px] text-slate-500">Dueño / Barbero Master</div>
              </button>

              <button
                type="button"
                onClick={() => handleLogin(undefined, 'recepcion@appgenda.io')}
                className="p-2.5 bg-slate-50 hover:bg-[#ECEAFF] border border-slate-200 hover:border-[#6255F5] rounded-xl text-left transition text-xs group cursor-pointer"
              >
                <div className="flex items-center gap-1 text-[#6255F5] font-bold">
                  <UserCheck className="w-3.5 h-3.5" />
                  <span>Recepción</span>
                </div>
                <div className="text-[10px] text-slate-500">Caja y Mostrador</div>
              </button>
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="w-full border-t border-slate-100" />
            <span className="bg-white px-3 text-[11px] text-slate-400 font-medium absolute">
              o con tu email
            </span>
          </div>

          {/* Formulario */}
          <form onSubmit={(e) => handleLogin(e)} className="space-y-4">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Email registrado</label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="tu@negocio.com"
                  className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                />
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Contraseña</label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#6255F5]/30 focus:border-[#6255F5]"
                />
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#6255F5] hover:bg-[#5144E5] text-white font-bold text-xs rounded-xl transition shadow-md shadow-[#6255F5]/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <span>Ingresando...</span>
              ) : (
                <>
                  <span>Iniciar sesión</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Footer Card */}
          <div className="pt-2 text-center text-xs text-slate-500 border-t border-slate-100">
            ¿No tenés una cuenta en Calenddy?{' '}
            <Link href="/registro" className="font-bold text-[#6255F5] hover:underline">
              Registrate acá
            </Link>
          </div>
        </div>
      </div>

      <footer className="py-6 text-center text-xs text-slate-400">
        © 2026 Calenddy · Acceso seguro protegido con tokens criptográficos.
      </footer>
    </div>
  );
}
